import axios from 'axios';
import {isPageLoadRead,pageLoadRetryDelay} from '../utils/pageLoadResilience.mjs';
import { deviceLocation } from './geofence';
import store from '../store';
import { trackActivity } from './activity';
import { logout, tokensRefreshed } from '../store/authSlice';
import { clearAuthState, getAccessToken, getRefreshToken } from '../utils/tokenStorage';

const baseURL = window.__THATTUKADA_OWNER_PORTAL__ ? '/api/v1' : (import.meta.env.VITE_API_URL || (import.meta.env.PROD?'/api/v1':'http://localhost:4000/api/v1'));

const api = axios.create({
  baseURL,
  withCredentials: true
});

// Retry only the explicit pre-write location challenge, never a failed payment or uncertain order.
api.interceptors.response.use((response) => response, async (error) => {
  if (error.response?.status !== 428 || error.response?.data?.code !== 'LOCATION_REQUIRED' || error.config?._locationRetried) throw error;
  const customerLocation = await deviceLocation();
  const body = typeof error.config.data === 'string' ? JSON.parse(error.config.data) : error.config.data;
  return api.request({ ...error.config, _locationRetried: true, data: { ...body, customerLocation } });
});

api.interceptors.request.use((config) => {
  if(isPageLoadRead(config)&&!config.timeout)config.timeout=12000;
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Only bounded, read-only page bootstrap requests may repeat during a server restart.
api.interceptors.response.use(response=>response,async error=>{const delay=pageLoadRetryDelay(error);if(delay==null)throw error;await new Promise((resolve,reject)=>{if(error.config.signal?.aborted)return reject(error);const stop=()=>{clearTimeout(timer);reject(error);};const timer=setTimeout(()=>{error.config.signal?.removeEventListener('abort',stop);resolve();},delay);error.config.signal?.addEventListener('abort',stop,{once:true});});return api.request({...error.config,_loadRetries:(error.config._loadRetries||0)+1});});

// Single-flight refresh: concurrent 401s share one refresh call instead of
// racing and invalidating each other's rotated tokens.
let refreshPromise = null;

const refreshSession = () => {
  if (!refreshPromise) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return Promise.reject(Object.assign(new Error('No refresh token'), { code: 'NO_REFRESH_SESSION' }));
    }
    // Plain axios so this call skips the interceptors above.
    refreshPromise = axios
      .post(`${baseURL}/auth/refresh`, { refreshToken }, { withCredentials: true,timeout:12000 })
      .then(({ data }) => {
        store.dispatch(tokensRefreshed(data.tokens));
        return data.tokens.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const forceLogout = () => {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    axios.post(`${baseURL}/auth/logout`, { refreshToken }, { withCredentials: true }).catch(() => {});
  }
  clearAuthState();
  store.dispatch(logout());
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || '';
    const skipAuthError = Boolean(error.config?.skipAuthError) || requestUrl.includes('/public/');
    const isAuthRoute = requestUrl.includes('/auth/');
    if (!isAuthRoute && ['SHOP_APPROVAL_PENDING','SHOP_REGISTRATION_REJECTED','SHOP_BLOCKED','SHOP_UNAVAILABLE'].includes(error.response?.data?.code)) {
      forceLogout();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !skipAuthError && !isAuthRoute && !error.config._retried) {
      try {
        const accessToken = await refreshSession();
        error.config._retried = true;
        error.config.headers.Authorization = `Bearer ${accessToken}`;
        return api.request(error.config);
      } catch (refreshError) {
        // A connection outage or temporary server failure does not revoke a session.
        if (refreshError.code === 'NO_REFRESH_SESSION' || [401, 403].includes(refreshError.response?.status)) forceLogout();
      }
    } else if (error.response?.status === 401 && !skipAuthError && !isAuthRoute) {
      forceLogout();
    }
    return Promise.reject(error);
  }
);

// Registered last so retried requests stay "active" until their final outcome (see activity.js).
trackActivity(api);

export default api;
